import React from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { iconDelete } from '@change-corgi/design-system-legacy/icons';

import type { PageRenderingButton, SupporterVideo } from '../../shared/types';

import { useDeleteVideo } from './hooks/useDeleteVideo';

type DeleteVideoProps = {
	petitionId: string;
	video: SupporterVideo;
	renderedOn: PageRenderingButton;
	/**
	 What happens after successfully deleting a video differs for different pages.
	 The 'afterDeleteAction' prop allows the page rendering of the delete
	 to specify what it wants to happen upon completion.
	*/
	afterDeleteAction: (videoId?: string) => void;
};
export const DeleteVideo: React.FC<DeleteVideoProps> = ({ petitionId, video, renderedOn, afterDeleteAction }) => {
	const {
		data: { canDeleteVideo },
		actions: { handleDeleteClick },
	} = useDeleteVideo({ petitionId, video, afterDeleteAction, renderedOn });

	const { translate } = useI18n();

	if (!canDeleteVideo) return null;

	return (
		<Button
			size="small"
			mode="icon"
			icon={iconDelete}
			aria-label={translate('corgi.components.petition_video.actions.aria_label.delete_video', {
				user_name: video.user?.shortDisplayName ?? '',
			})}
			data-testid={`supporter-video-${video.id}-delete-button`}
			sx={{
				// These styles override the design-system-legacy theme because they were designed when the
				// videos feature and design systems were being redesigned. When we move fully to sunrise these
				// can be reevaluated and replaced with ds-next standard buttons.
				'& > *': { color: 'primary-black' },
				border: 'none',
				background: 'primary-greyBackground',
				'&:hover': {
					background: 'rgba(0,0,0,0.1)',
				},
			}}
			onClick={handleDeleteClick}
		>
			Delete
		</Button>
	);
};
